'use client';

// The whole shop page. It lives on Shopify at shop.sfalexandria.com and
// nowhere else: shopify/entry.tsx is its only mount point (the Next site's
// /shop route was removed). No Next imports here — the canvas component is
// injected by the host.
import { Component, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { ALL_PRODUCTS as PRODUCTS, cartUrl, imageUrl } from './products';
import { HandwrittenMoney, HandwrittenNumber, HandwrittenTitle, handFrameStyle } from './Handwriting';
import ShopWordmark from './ShopWordmark';
import ShirtProduct from './ShirtProduct';
import { SHIRT_ID, ShirtSelection, shirtImage } from './apparel';
import { CartLine, lineKey, normalizeCart, checkoutShirtCart } from './cart';
type CanvasComponent = React.ComponentType<{
  focusedId: string | null;
  onSelect: (id: string | null) => void;
  onShelfReady: () => void;
  onProductReady: (id: string) => void;
  cartOpen?: boolean;
  onUnavailable: () => void;
}>;
const CART_KEY = 'alexandria-shop-cart-v1';
function readCart(): CartLine[] {
  try {
    return normalizeCart(JSON.parse(localStorage.getItem(CART_KEY) ?? '[]'));
  } catch {
    return [];
  }
}
class SceneBoundary extends Component<{
  children: React.ReactNode;
  onUnavailable: () => void;
}, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch() {
    this.props.onUnavailable();
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}
export default function ShopShell({
  Canvas3D
}: {
  Canvas3D: CanvasComponent;
}) {
  const [staticHeader] = useState(() => document.querySelector<HTMLElement>('[data-shop-static-header]'));
  const [sceneRequested, setSceneRequested] = useState(false);
  useEffect(() => {
    // Let a complete paint happen before starting the 3D engine. A timeout
    // scheduled in the first frame can run before that frame is presented.
    // A second animation frame gives the static header a paint opportunity.
    let nextFrame: number | undefined;
    const frame = requestAnimationFrame(() => {
      nextFrame = requestAnimationFrame(() => setSceneRequested(true));
    });
    return () => {
      cancelAnimationFrame(frame);
      if (nextFrame !== undefined) cancelAnimationFrame(nextFrame);
    };
  }, []);
  const [focusedId, setFocusedId] = useState<string | null>(() => new URLSearchParams(location.search).get('product') === SHIRT_ID || location.pathname.includes('/products/' + SHIRT_ID) ? SHIRT_ID : null);
  const [cart, setCart] = useState<CartLine[]>(readCart);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const onUnavailable = useCallback(() => setUnavailable(true), []);
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {/* Private browsing can disable storage. */}
  }, [cart]);
  const [cartOpen, setCartOpen] = useState(false);
  const cartDialog = useRef<HTMLElement>(null);
  const cartCloseButton = useRef<HTMLButtonElement>(null);
  const cartOpenButton = useRef<HTMLButtonElement>(null);
  const cartReturnFocus = useRef<HTMLElement | null>(null);
  const selectedId = useRef<string | null>(focusedId);
  // Nothing on the page announces itself before the thing it is about is on
  // screen: the handwritten note waits for the shelf to load, and the
  // product's title, description, price and cart button wait for its zine to finish
  // flying out of the shelf.
  const [shelfReady, setShelfReady] = useState(false);
  // A direct product link can show its photo and controls immediately;
  // shelf clicks still wait for the take-off animation.
  const [readyId, setReadyId] = useState<string | null>(focusedId);
  const [shirtMounted, setShirtMounted] = useState(focusedId === SHIRT_ID);
  useEffect(() => {
    if (focusedId === SHIRT_ID) {
      setShirtMounted(true);
      return;
    }
    // Keep the photo in place while it dissolves back into the returning tee.
    const timer = setTimeout(() => setShirtMounted(false), 200);
    return () => clearTimeout(timer);
  }, [focusedId]);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const product = PRODUCTS.find(p => p.id === focusedId) ?? null;
  // Retain the last product's content so the existing opacity transition can
  // finish when selection clears. The hidden panel remains inert.
  const detailsProduct = PRODUCTS.find(p => p.id === detailsId) ?? null;
  const titleSize = detailsProduct?.id === 'training-the-idea-muscle' ? {
    width: 1932,
    height: 969,
    src: 'hand/product-title-riley.svg'
  } : {
    width: 1793,
    height: 816,
    src: 'hand/product-title-farza.svg'
  };
  const copyAspect = titleSize.height / titleSize.width + (detailsProduct ? .82 * detailsProduct.descriptionImage.height / detailsProduct.descriptionImage.width : 0);
  const panelSpace = '100cqh - var(--description-gap) - var(--control-gap) - var(--panel-padding) - env(safe-area-inset-bottom, 0px)';
  const shirtShown = focusedId === SHIRT_ID && (unavailable || readyId === SHIRT_ID);
  const productShown = !!product && product.kind !== 'shirt' && (unavailable || readyId === product.id);
  const onSelect = useCallback((id: string | null) => {
    // Clicking the already selected miniature must not hide its controls:
    // the model only reports arrival once per selection.
    if (selectedId.current === id) return;
    selectedId.current = id;
    setReadyId(null);
    setFocusedId(id);
    const url = new URL(location.href);
    if (id === SHIRT_ID) url.searchParams.set('product', id);else {
      url.searchParams.delete('product');
      if (url.pathname.includes('/products/' + SHIRT_ID)) url.pathname = '/';
    }
    history.replaceState(null, '', url);
    if (id && id !== SHIRT_ID) setDetailsId(id);
  }, []);
  const onShelfReady = useCallback(() => {
    setShelfReady(true);
    for (const file of ['hand/product-title-farza.svg', 'hand/product-title-riley.svg', ...PRODUCTS.filter(item => item.kind !== 'shirt').map(item => item.descriptionImage.src), 'hand/cart-button.svg', 'hand/zine-sale-postit.webp']) {
      const image = new Image();
      image.src = imageUrl(file);
    }
  }, []);
  const onProductReady = useCallback((id: string) => setReadyId(id), []);
  const openCart = () => {
    cartReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCartOpen(true);
  };
  const addToCart = (id: string, selection?: ShirtSelection) => {
    const key = lineKey({
      productId: id,
      selection
    });
    setCart(lines => {
      const hit = lines.find(l => lineKey(l) === key);
      return hit ? lines.map(l => lineKey(l) === key ? {
        ...l,
        qty: Math.min(99, l.qty + 1)
      } : l) : [...lines, {
        productId: id,
        qty: 1,
        selection
      }];
    });
    openCart();
  };

  // minus at qty 1 removes the line; an empty cart closes (and hides) itself
  const bumpQty = (key: string, delta: number) => {
    setCart(lines => {
      const next = lines.map(l => lineKey(l) === key ? {
        ...l,
        qty: Math.min(99, l.qty + delta)
      } : l).filter(l => l.qty > 0);
      return next;
    });
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (cartOpen) setCartOpen(false);else onSelect(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cartOpen, onSelect]);
  const count = cart.reduce((n, l) => n + l.qty, 0);
  const cartVisible = cartOpen && count > 0;
  useEffect(() => {
    if (staticHeader) staticHeader.inert = cartVisible;
  }, [staticHeader, cartVisible]);
  useEffect(() => {
    if (cart.length === 0) setCartOpen(false);
  }, [cart.length]);
  useEffect(() => {
    if (cartVisible) {
      cartCloseButton.current?.focus({
        preventScroll: true
      });
    } else if (cartReturnFocus.current) {
      if (cartReturnFocus.current?.isConnected) {
        cartReturnFocus.current.focus({
          preventScroll: true
        });
      } else {
        cartOpenButton.current?.focus({
          preventScroll: true
        });
      }
      cartReturnFocus.current = null;
    }
  }, [cartVisible]);
  useEffect(() => {
    if (!cartVisible) return;
    const containFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const controls = cartDialog.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', containFocus);
    return () => document.removeEventListener('keydown', containFocus);
  }, [cartVisible]);
  const subtotal = cart.reduce((sum, l) => {
    const p = PRODUCTS.find(pr => pr.id === l.productId);
    return sum + (p ? parseFloat(p.price.replace('$', '')) * l.qty : 0);
  }, 0);
  const hasShirt = cart.some(line => line.productId === SHIRT_ID);
  const checkout = async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    setCheckoutError('');
    try {
      await checkoutShirtCart(cart);
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Checkout failed. Please try again.');
      setCheckoutBusy(false);
    }
  };
  const checkoutHref = cartUrl(cart.map(l => {
    const p = PRODUCTS.find(pr => pr.id === l.productId);
    return p ? {
      variantId: p.variantId,
      qty: l.qty
    } : null;
  }).filter((l): l is {
    variantId: string;
    qty: number;
  } => l !== null));
  return <main className="shop-viewport fixed inset-0 overflow-hidden bg-white" aria-label="Alexandria shop">
      <div className="absolute inset-0" inert={cartVisible}>
      <div className="absolute inset-0">
        {sceneRequested && !unavailable && <SceneBoundary onUnavailable={onUnavailable}><Suspense fallback={null}><Canvas3D focusedId={focusedId} onSelect={onSelect} onShelfReady={onShelfReady} onProductReady={onProductReady} cartOpen={cartOpen && count > 0} onUnavailable={onUnavailable} /></Suspense></SceneBoundary>}
        {!shelfReady && !unavailable && !focusedId && <p role="status" className="absolute bottom-6 left-0 right-0 text-center font-twk text-sm text-neutral-500">Loading the bookshelf…</p>}
        {unavailable && <div className={`absolute inset-0 flex items-center justify-center gap-5 p-6 pt-20 ${product ? 'shop-fallback-focused' : ''}`}>
          {(product ? [product] : PRODUCTS).map(item => <button key={item.id} onClick={() => onSelect(product ? null : item.id)} className="flex max-h-full max-w-[40%] flex-col items-center gap-3 border-0 bg-transparent font-twk text-sm" aria-label={product ? 'Back to shelf' : item.title}>
            <img src={imageUrl(item.image)} alt={item.title} className="max-h-[40vh] max-w-full object-contain" />
            <span>{product ? 'Back to shelf' : item.title}</span>
          </button>)}
        </div>}
      </div>

      {/* header */}
      {!staticHeader && <header className="shop-header">
        <a className="shop-back-to-blog" href="https://sfalexandria.com">
          <span className="shop-blog-arrow" aria-hidden="true">
            <svg viewBox="0 0 16 12" fill="none" focusable="false">
              <path d="M15 6H1M6 1 1 6l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>Back to blog</span>
        </a>
        <ShopWordmark />
      </header>}

      <nav aria-label="Browse products" className="x10l6tqk xa9119d x1huftch x1qpfodn xn5hqff x78zum5 x1a02dak x167g77z xg01cxk x1euntei x47corl xbt4iw">
        {PRODUCTS.map(item => <button key={item.id} onClick={() => onSelect(item.id)} className="x1gg8mnh xc7ga6q x83z2og xronout xmkeg23 x1y0btm7 x13nfsaz xepszhr x1ypdohk">
            {item.title}
          </button>)}
        {product && <button onClick={() => onSelect(null)} className="x1gg8mnh xc7ga6q x83z2og xronout xmkeg23 x1y0btm7 x13nfsaz xepszhr x1ypdohk">
            Back to shelf
          </button>}
      </nav>

      {(focusedId === SHIRT_ID || shirtMounted) && <ShirtProduct visible={shirtShown} leaving={focusedId !== SHIRT_ID} onAdd={selection => addToCart(SHIRT_ID, selection)} />}

      {/* Fixed within each viewport; resizing recomputes the composition. */}
      <div data-shelf-annotation className="shop-shelf-annotation" style={{
        visibility: product || !shelfReady || unavailable ? 'hidden' : 'visible'
      }}>
        <img data-shelf-note src={imageUrl('hand/note-real-shelf.webp')} srcSet={[96, 192, 384, 640].map(width => `${imageUrl(width === 640 ? 'hand/note-real-shelf.webp' : `hand/note-real-shelf-${width}.webp`)} ${width}w`).join(', ')} sizes="min(13.1vw, 9.74vh)" decoding="async" alt="the real shelf from my house, btw" width={640} height={636} draggable={false} />
        <span data-shelf-arrow aria-hidden="true">
          <img src={imageUrl('hand/magazine-arrow-source.svg')} alt="" draggable={false} />
        </span>
      </div>

      {/* the store page: fades in as the shelf moves aside (no back
          button — clicking the shelf or the empty page goes back) */}
      <aside data-shop-sheet inert={!productShown} aria-hidden={!productShown} aria-label={detailsProduct?.title ?? 'Product details'} className={`shop-product-sheet absolute z-20 transition-opacity duration-200 ease-out ${productShown ? 'opacity-100' : 'pointer-events-none opacity-0'}`}>
        {detailsProduct && <div className="shop-product-content" style={{
          // Fit both the artwork and the button's minimum 44px touch target.
          maxWidth: `min(calc((${panelSpace}) / ${copyAspect + .78 * 166 / 605}), calc((${panelSpace} - 44px) / ${copyAspect}))`
        }}>
            <h1 className="sr-only">{detailsProduct.title}</h1>
            <img src={imageUrl(titleSize.src)} width={titleSize.width} height={titleSize.height} alt="" draggable={false} className="shop-product-title" />
            <img src={imageUrl(detailsProduct.descriptionImage.src)} width={detailsProduct.descriptionImage.width} height={detailsProduct.descriptionImage.height} alt={detailsProduct.blurb} draggable={false} className="shop-product-description" />
            <p className="sr-only">On sale for {detailsProduct.price}, originally $25.00</p>
            <button onClick={() => addToCart(detailsProduct.id)} aria-label="Add to cart" className="shop-add-to-cart">
              <img className="shop-cart-base" src={imageUrl('hand/cart-button.svg')} width={605} height={166} alt="" draggable={false} />
              <img className="shop-cart-face" src={imageUrl('hand/cart-button.svg')} width={605} height={166} alt="" draggable={false} />
            </button>
          </div>}
      </aside>
      <img data-product-price src={imageUrl('hand/zine-sale-postit.webp')} width={320} height={321} alt="" draggable={false} className={`shop-product-price ${unavailable ? 'shop-product-price-fallback' : ''} pointer-events-none absolute z-20 select-none transition-opacity duration-200 ${productShown ? 'opacity-100' : 'opacity-0'}`} />

      {/* the cart only exists while it holds something: no dedicated cart
          button — just this small tab to reopen the drawer. On phones it
          sits ABOVE the "real shelf" note (which starts at y=64): the note
          owns the top-right corner from there down to the shelf. */}
      {count > 0 && !cartOpen && <button ref={cartOpenButton} onClick={openCart} aria-label="Open cart" className="shop-open-cart absolute right-4 top-2 z-30 flex h-12 w-12 cursor-pointer items-center justify-center border border-neutral-300 bg-white text-neutral-800 shadow-sm transition-transform hover:scale-[1.06] md:right-7">
          <img className="shop-cart-icon" src={imageUrl('hand/cart-icon.jpg')} width={632} height={578} alt="" draggable={false} />
          <span className="shop-cart-count">
            <HandwrittenNumber value={count} />
          </span>
        </button>}
      </div>

      {/* side cart drawer */}
      {cartVisible && <button aria-label="Close shopping cart" tabIndex={-1} onClick={() => setCartOpen(false)} className="absolute inset-0 z-30 cursor-default border-0 bg-transparent p-0" />}
      <aside ref={cartDialog} role="dialog" aria-modal={cartVisible ? true : undefined} aria-label="Shopping cart" inert={!cartVisible} aria-hidden={!cartVisible} className={`absolute inset-y-0 right-0 z-40 flex w-[22rem] max-w-[88vw] flex-col border-l border-neutral-200 bg-white transition-transform duration-200 ease-out motion-reduce:transition-none ${cartVisible ? 'translate-x-0 shadow-xl' : 'translate-x-full shadow-none'}`}>
        {count > 0 && <>
        <div className="flex items-center justify-between border-b border-neutral-200 p-5">
          <h2 className="relative m-0 font-guyot text-2xl font-[600] text-black">
            {/* Keep the original text's dimensions as the artwork's sizing box. */}
            <span className="invisible" aria-hidden="true">Cart</span>
            <img className="absolute inset-0 h-full w-full object-contain" src={imageUrl('hand/cart-heading.jpg')} width={1142} height={463} loading="lazy" decoding="async" alt="Cart" draggable={false} />
          </h2>
          <button ref={cartCloseButton} onClick={() => setCartOpen(false)} aria-label="Close cart" className="-m-2 flex h-11 w-11 cursor-pointer items-center justify-center border-none bg-transparent text-2xl leading-none text-neutral-500 hover:text-black">
            <img src={imageUrl('hand/cart-close.jpg')} width={306} height={393} alt="" draggable={false} style={{
              width: 14,
              height: 14,
              objectFit: 'contain'
            }} />
          </button>
        </div>
        <div className="x845mor x2lwn1j x1odjw0f xish69e x1qhigcl">
          {cart.map(line => {
            const p = PRODUCTS.find(pr => pr.id === line.productId);
            if (!p) return null;
            return <div key={lineKey(line)} className="mb-6 flex gap-4">
                <img src={imageUrl(line.selection ? shirtImage(line.selection.color) : p.image)} alt={p.title} width={p.id === 'training-the-idea-muscle' ? 240 : 254} height={360} className="h-24 w-auto self-start border border-neutral-200 object-cover" />
                <div className="shop-cart-item-details">
                <HandwrittenTitle title={p.title} artwork={p.kind === 'shirt' ? 'shirt' : p.id === 'training-the-idea-muscle' ? 'riley' : 'farza'} />
                {p.kind === 'shirt' && <p className="shirt-cart-options">{line.selection?.color} / {line.selection?.size}</p>}
                <p className="shop-cart-price"><HandwrittenMoney value={Number(p.price.replace('$', ''))} hideZeroCents={p.kind === 'shirt'} /></p>
                <div className="shop-quantity" style={handFrameStyle('quantity-box')}>
                  <button onClick={() => bumpQty(lineKey(line), -1)} aria-label={`Decrease quantity of ${p.title}`} className="shop-quantity-button">
                    <img src={imageUrl('hand/minus.svg')} alt="" draggable={false} />
                  </button>
                  <span className="shop-quantity-value" aria-live="polite" aria-atomic="true">
                    <HandwrittenNumber value={line.qty} />
                  </span>
                  <button onClick={() => bumpQty(lineKey(line), 1)} disabled={line.qty >= 99} aria-label={`Increase quantity of ${p.title}`} className="shop-quantity-button">
                    <img src={imageUrl('hand/plus.svg')} alt="" draggable={false} />
                  </button>
                </div>
                </div>
              </div>;
          })}
        </div>
        <div className="x2lah0s x178xt8z x13fuv20 x10h6knf x1qhigcl x39ggul">
          <div className="shop-subtotal-row">
            <img className="shop-subtotal-label" src={imageUrl('hand/subtotal.svg')} width={212} height={56} alt="Subtotal" draggable={false} loading="lazy" />
            <span className="shop-subtotal-amount" aria-live="polite" aria-atomic="true"><HandwrittenMoney value={subtotal} /></span>
          </div>
          <a href={hasShirt ? undefined : checkoutHref} role={hasShirt ? 'button' : undefined} tabIndex={0} onClick={hasShirt ? () => {
            void checkout();
          } : undefined} onKeyDown={hasShirt ? e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void checkout();
            }
          } : undefined} aria-disabled={checkoutBusy} className="shop-checkout" aria-label={checkoutBusy ? 'Opening checkout' : 'Checkout'}>
            <img className="shop-cart-base" src={imageUrl('hand/checkout-button.svg')} width={531} height={155} alt="" draggable={false} />
            <img className="shop-cart-face" src={imageUrl('hand/checkout-button.svg')} width={531} height={155} alt="" draggable={false} />
          </a>
          {checkoutError && <p role="alert" className="shop-checkout-error">{checkoutError}</p>}
        </div>
        </>}
      </aside>
    </main>;
}